import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MyOfferDto } from '@rw/shared';
import { useNavigate } from 'react-router';
import { Page } from '../../components/Layout';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { Card } from '../../components/ui';
import { useT } from '../../i18n';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { toast } from '../../stores/toasts';
import { offerKeys, useMyOffers } from './offers-api';

/** Profile → My offers: the codes this runner earned from sponsors. */
export function OffersPage() {
  const navigate = useNavigate();
  const t = useT();
  const q = useMyOffers();
  return (
    <Page>
      <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
        ← {t('Back')}
      </button>
      <h1 className="font-display text-4xl font-extrabold uppercase">{t('My offers')}</h1>
      <p className="text-sm text-ink-400">
        {t(
          'Gifts from sponsors: capture their sectors on the map or finish the events they sponsor.',
        )}
      </p>
      {q.isPending ? (
        <LoadingState />
      ) : q.isError && !q.data ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !q.data?.length ? (
        <EmptyState
          icon="🎁"
          title={t('No offers yet')}
          body={t('Sponsored sectors and events show their gifts. Run to earn one!')}
        />
      ) : (
        <div className="mt-4 space-y-3" data-testid="my-offers">
          {q.data.map((o) => (
            <OfferCard key={o.id} offer={o} />
          ))}
        </div>
      )}
    </Page>
  );
}

function OfferCard({ offer: o }: { offer: MyOfferDto }) {
  const t = useT();
  const qc = useQueryClient();
  const used = useMutation({
    mutationFn: (v: boolean) => api<MyOfferDto>(`/me/offers/${o.id}/used`, { body: { used: v } }),
    // Stays pending until the list is fresh, so the checkbox never flickers back.
    onSuccess: () => qc.invalidateQueries({ queryKey: offerKeys.mine }),
  });
  const done = !!o.usedAt || o.expired;
  async function copy() {
    try {
      await navigator.clipboard.writeText(o.code);
      toast({ kind: 'success', title: t('Code copied') });
    } catch {
      /* clipboard unavailable: the code is on screen */
    }
  }
  return (
    <div data-testid="my-offer">
      <Card className={`space-y-3 ${done ? 'opacity-60' : ''}`}>
        <div className="flex items-center gap-3">
          <div className="relative h-12 w-20 shrink-0 overflow-hidden rounded-lg border border-ink-700 bg-ink-900">
            {o.brand.logoUrl ? (
              <img
                src={o.brand.logoUrl}
                alt={o.brand.name}
                className="absolute inset-0 h-full w-full object-contain p-1"
              />
            ) : (
              <span className="absolute inset-0 grid place-items-center px-1 text-center text-xs font-bold">
                {o.brand.name}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs text-ink-400">{o.brand.name}</div>
            <div className="font-semibold">{o.title}</div>
          </div>
        </div>
        {o.body ? <p className="whitespace-pre-line text-sm text-ink-300">{o.body}</p> : null}
        <button
          onClick={copy}
          className="flex w-full items-center justify-between rounded-xl border border-dashed border-gold/60 bg-gold/10 px-4 py-3 text-left"
          data-testid="my-offer-code"
        >
          <span className="font-mono text-xl font-bold tracking-wider text-gold">{o.code}</span>
          <span className="text-xs font-semibold text-gold">{t('Copy')}</span>
        </button>
        {o.terms ? <p className="whitespace-pre-line text-xs text-ink-400">{o.terms}</p> : null}
        <div className="flex items-center justify-between text-xs">
          <span className={o.expired ? 'text-enemy' : 'text-ink-400'}>
            {t(o.expired ? 'Expired {date}' : 'Valid until {date}', {
              date: formatDate(o.expiresAt),
            })}
          </span>
          <label className="flex items-center gap-2 font-semibold text-ink-200">
            <input
              type="checkbox"
              checked={used.isPending ? !!used.variables : !!o.usedAt}
              disabled={used.isPending}
              onChange={(e) => used.mutate(e.target.checked)}
              className="size-4"
              data-testid="my-offer-used"
            />
            {t('Used')}
          </label>
        </div>
      </Card>
    </div>
  );
}
